import { Link } from '@tanstack/react-router'
import { ArrowLeftIcon } from 'lucide-react'

import { Button } from '@/components/button/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/card/card'
import { Form, FormField, FormItem, FormLabel } from '@/components/form/form'
import { PasswordInput } from '@/components/input/password-input'

import { cn } from '@/lib/utils'

import { IndependentPageTopBar } from '../ui/individual-page-top-bar/individual-page-top-bar'
import { useChangePasswordPage } from './use-page'

export const ChangePasswordPage = () => {
  const {
    form,
    formErrors,
    isUpdatingUserPassword,
    isDisabledSaveButton,
    isDisabledCancelButton,
    handleClickGoBackButton,
    handleSubmitForm,
    handleClickCancelButton,
  } = useChangePasswordPage()

  return (
    <>
      {/* Top bar */}
      <IndependentPageTopBar />
      <main>
        {/* Go back button */}
        <div className={cn('px-3 py-2')}>
          <Button
            variant="ghost"
            className={cn('text-lg font-semibold text-foreground', 'space-x-2')}
            onClick={handleClickGoBackButton}
          >
            <ArrowLeftIcon size={16} />
            <span>Go back to Settings</span>
          </Button>
        </div>

        {/* Card */}
        <div className={cn('px-4 py-[100px]')}>
          <Card className={cn('max-w-[420px]', 'mx-auto')}>
            <CardHeader>
              <CardTitle>Change Password</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={cn('mt')}>
                <p className={cn('text-sm font-normal text-muted-foreground')}>
                  If you signed up with Google and haven&apos;t set a password yet,&nbsp;
                  <Link
                    to="/forgot-password"
                    search={{
                      isSettingInitialGooglePassword: true,
                    }}
                    className={cn('text-blue-600 underline')}
                  >
                    please set one here.
                  </Link>
                </p>
              </div>

              <div className={cn('mt-6')}>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(handleSubmitForm)} className={cn('space-y-4')}>
                    <FormField
                      control={form.control}
                      name="currentPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Current password</FormLabel>
                          <PasswordInput {...field} />
                          {formErrors.currentPassword && (
                            <p className={cn('text-sm font-normal text-rose-600', '!mt-1')}>
                              {formErrors.currentPassword.message}
                            </p>
                          )}
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="newPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>New password</FormLabel>
                          <PasswordInput {...field} />
                          {formErrors.newPassword && (
                            <p className={cn('text-sm font-normal text-rose-600', '!mt-1')}>
                              {formErrors.newPassword.message}
                            </p>
                          )}
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="newPasswordConfirm"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm new password</FormLabel>
                          <PasswordInput {...field} />
                          {formErrors.newPasswordConfirm && (
                            <p className={cn('text-sm font-normal text-rose-600', '!mt-1')}>
                              {formErrors.newPasswordConfirm.message}
                            </p>
                          )}
                        </FormItem>
                      )}
                    />

                    <p className={cn('mt-3', 'text-sm font-normal text-muted-foreground')}>
                      You will be redirected to the login page.
                    </p>

                    <div className={cn('flex gap-x-3', 'mt-6')}>
                      <Button
                        type="submit"
                        variant="default"
                        disabled={isDisabledSaveButton}
                        loading={isUpdatingUserPassword}
                        className={cn('w-2/3')}
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        className={cn('w-1/3')}
                        disabled={isDisabledCancelButton}
                        onClick={handleClickCancelButton}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </Form>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </>
  )
}
